import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { submitApplication } from "../../lib/api";
import { T } from "../../theme";

/**
 * Banco de talentos — candidatura espontânea (sem vaga específica).
 *
 * Reaproveita o endpoint /jobs/apply enviando jobId vazio e um jobTitle do tipo
 * "Banco de talentos — <área>". O backend grava como uma candidatura normal
 * (job_id nulo) e roteia o e-mail para o RH central.
 */
const AREAS = [
  "Tecnologia", "Produto", "Design", "Dados", "Marketing", "Growth",
  "Comercial / Vendas", "Operações", "Financeiro", "Conteúdo", "People & Culture", "Outra",
];

const fadeUp = keyframes`from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); }`;

const Page = styled.div`min-height: 100vh; display: flex; flex-direction: column; background: ${T.bg};`;
const Wrap = styled.main`
  flex: 1; width: 100%; max-width: 720px; margin: 0 auto;
  padding: clamp(28px, 5vw, 52px) clamp(16px, 4vw, 32px) 96px;
  animation: ${fadeUp} .4s ease both;
`;
const Back = styled.button`
  display: inline-flex; align-items: center; gap: 6px; background: none; border: none;
  color: ${T.muted}; font-size: .82rem; font-weight: 600; cursor: pointer; margin-bottom: 28px; padding: 6px 0;
  &:hover { color: ${T.text}; }
`;
const Eyebrow = styled.span`
  display: inline-flex; align-items: center; gap: 5px; font-size: .71rem; font-weight: 700;
  padding: 3px 11px; border-radius: 999px; background: rgba(155,81,255,.12); color: var(--purple);
  border: 1px solid rgba(155,81,255,.25); margin-bottom: 14px;
`;
const Title = styled.h1`
  font-size: clamp(1.7rem, 4vw, 2.4rem); font-weight: 900; letter-spacing: -.03em;
  color: ${T.text}; line-height: 1.12; margin-bottom: 12px;
`;
const Lead = styled.p`font-size: .95rem; color: ${T.muted}; line-height: 1.65; margin-bottom: 32px; max-width: 560px;`;

const Card = styled.div`
  background: ${T.card}; border: 1.5px solid ${T.border}; border-radius: 18px;
  padding: clamp(20px, 4vw, 30px);
`;
const Grid = styled.div`display: grid; grid-template-columns: ${(p) => (p.$cols === 2 ? "1fr 1fr" : "1fr")}; gap: 16px;
  @media (max-width: 560px) { grid-template-columns: 1fr; }`;
const Field = styled.div`display: flex; flex-direction: column; gap: 7px; margin-bottom: 16px;`;
const Label = styled.label`font-size: .8rem; font-weight: 600; color: ${T.text};`;
const Req = styled.span`color: var(--purple);`;
const inputCss = `
  width: 100%; box-sizing: border-box; font-family: inherit; font-size: .9rem;
  padding: 11px 13px; border-radius: 10px; outline: none; transition: border-color .14s;
`;
const Input = styled.input`${inputCss} color: ${T.text}; background: ${T.bg}; border: 1.5px solid ${T.border};
  &:focus { border-color: var(--purple); }`;
const Select = styled.select`${inputCss} color: ${T.text}; background: ${T.bg}; border: 1.5px solid ${T.border}; cursor: pointer;
  &:focus { border-color: var(--purple); }`;
const Textarea = styled.textarea`${inputCss} color: ${T.text}; background: ${T.bg}; border: 1.5px solid ${T.border};
  resize: vertical; line-height: 1.5; &:focus { border-color: var(--purple); }`;
const FileLabel = styled.label`
  display: flex; align-items: center; gap: 10px; padding: 12px 14px; border-radius: 10px;
  border: 1.5px dashed ${T.border}; color: ${T.muted}; font-size: .85rem; cursor: pointer;
  &:hover { border-color: var(--purple); color: ${T.text}; }
  &:focus-within { border-color: var(--purple); color: ${T.text}; box-shadow: 0 0 0 3px var(--purple-soft); }
  /* input focável por teclado, mas visualmente oculto (a11y) */
  input { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
`;
const ConsentRow = styled.label`
  display: flex; gap: 11px; align-items: flex-start; margin-top: 4px; padding: 14px 16px;
  border: 1.5px solid ${(p) => (p.$on ? "rgba(155,81,255,.4)" : T.border)}; border-radius: 12px;
  background: ${(p) => (p.$on ? "var(--purple-soft)" : T.bg)}; cursor: pointer;
  font-size: .82rem; color: ${T.muted}; line-height: 1.55;
  input { accent-color: var(--purple); width: 17px; height: 17px; margin-top: 1px; flex-shrink: 0; }
  a { color: var(--purple); font-weight: 600; }
`;
const ErrorText = styled.p`color: #f87171; font-size: .8rem; margin-top: 6px;`;
const Submit = styled.button`
  margin-top: 22px; width: 100%; background: var(--purple); color: #fff; border: none;
  font-size: .98rem; font-weight: 800; padding: 14px; border-radius: 12px; cursor: pointer;
  box-shadow: 0 4px 18px rgba(155,81,255,.32); transition: filter .14s, transform .12s;
  &:hover { filter: brightness(1.1); transform: translateY(-2px); }
  &:disabled { opacity: .5; cursor: not-allowed; transform: none; box-shadow: none; }
`;

export default function BancoTalentos() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", linkedin: "", portfolio: "", area: "", about: "" });
  const [file, setFile] = useState(null);
  const [fileErr, setFileErr] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    document.title = "Banco de talentos — Guebly Carreiras";
    return () => { document.title = "Guebly Carreiras — Vagas abertas"; };
  }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleFile = (e) => {
    const f = e.target.files[0] || null;
    if (f && f.size > 10 * 1024 * 1024) { setFileErr("O arquivo excede 10 MB."); setFile(null); }
    else { setFileErr(""); setFile(f); }
  };

  const valid = form.name.trim() && /\S+@\S+\.\S+/.test(form.email) && form.area && consent;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!valid) { setErr("Preencha nome, e-mail, área e aceite o uso dos dados."); return; }
    setSubmitting(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone || "");
      fd.append("linkedin", form.linkedin || "");
      fd.append("portfolio", form.portfolio || "");
      fd.append("about", form.about || "");
      fd.append("jobId", "");
      fd.append("jobTitle", `Banco de talentos — ${form.area}`);
      fd.append("source", "banco-talentos");
      fd.append("lgpdConsent", String(consent));
      if (file) fd.append("curriculum", file);
      const result = await submitApplication(fd);
      if (result && result.ok === false) { setErr(result.message || "Não foi possível enviar. Tente novamente."); return; }
      navigate("/obrigado");
    } catch {
      setErr("Erro ao enviar. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <Header />
      <Wrap id="main-content">
        <Back onClick={() => navigate("/")} aria-label="Voltar para a home">
          <span aria-hidden="true">←</span> Voltar
        </Back>

        <Eyebrow>Banco de talentos</Eyebrow>
        <Title>Não achou a vaga ideal? Entra pro nosso radar.</Title>
        <Lead>
          Deixe seu perfil no nosso banco de talentos. Quando abrir uma vaga aderente
          ao que você faz, a gente te chama — sem você precisar ficar voltando aqui.
        </Lead>

        <Card>
          <form onSubmit={onSubmit}>
            <Grid $cols={2}>
              <Field>
                <Label htmlFor="t-name">Nome completo <Req aria-hidden="true">*</Req></Label>
                <Input id="t-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Seu nome" required />
              </Field>
              <Field>
                <Label htmlFor="t-email">E-mail <Req aria-hidden="true">*</Req></Label>
                <Input id="t-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="voce@email.com" required />
              </Field>
            </Grid>
            <Grid $cols={2}>
              <Field>
                <Label htmlFor="t-phone">Telefone / WhatsApp</Label>
                <Input id="t-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(11) 90000-0000" />
              </Field>
              <Field>
                <Label htmlFor="t-area">Área de interesse <Req aria-hidden="true">*</Req></Label>
                <Select id="t-area" value={form.area} onChange={(e) => set("area", e.target.value)} required>
                  <option value="">Selecionar…</option>
                  {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
                </Select>
              </Field>
            </Grid>
            <Grid $cols={2}>
              <Field>
                <Label htmlFor="t-linkedin">LinkedIn</Label>
                <Input id="t-linkedin" value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} placeholder="linkedin.com/in/voce" />
              </Field>
              <Field>
                <Label htmlFor="t-portfolio">Portfólio / site</Label>
                <Input id="t-portfolio" value={form.portfolio} onChange={(e) => set("portfolio", e.target.value)} placeholder="seusite.com" />
              </Field>
            </Grid>
            <Field>
              <Label htmlFor="t-about">Conte um pouco sobre você</Label>
              <Textarea id="t-about" rows={4} value={form.about} onChange={(e) => set("about", e.target.value)} placeholder="O que você faz, sua experiência e o que procura…" />
            </Field>
            <Field>
              <Label>Currículo (PDF, opcional)</Label>
              <FileLabel>
                <span aria-hidden="true">📎</span>
                {file ? file.name : "Anexar currículo em PDF"}
                <input type="file" accept="application/pdf" onChange={handleFile} />
              </FileLabel>
              {fileErr && <ErrorText>{fileErr}</ErrorText>}
            </Field>

            <ConsentRow $on={consent}>
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} aria-describedby="t-lgpd" />
              <span id="t-lgpd">
                Autorizo a Guebly LTDA a tratar os meus dados pessoais para fins de
                recrutamento e seleção, conforme a{" "}
                <a href="https://www.guebly.com.br/politica-de-privacidade" target="_blank" rel="noreferrer">Política de Privacidade</a>.
              </span>
            </ConsentRow>

            {err && <ErrorText role="alert">{err}</ErrorText>}

            <Submit type="submit" disabled={submitting || !valid}>
              {submitting ? "Enviando…" : "Entrar no banco de talentos →"}
            </Submit>
          </form>
        </Card>
      </Wrap>
      <Footer />
    </Page>
  );
}
